import gsap, { ScrollTrigger } from "@lib/gsap";
import { isStaging } from "@/webflow/is-staging";
import { onDestroy } from "@/modules/_";
import { scrollTriggerDefaults } from "@lib/animations";
import { Resize } from "@/lib/subs";
import { Scroll } from "@lib/scroll";

export default function (element, dataset) {
  // --- Scroll-triggered line animations ---
  const realHeader = element.querySelector(
    ".tabs_header:not(.u-cover-absolute)",
  );
  const lines = {
    top: realHeader.querySelector(".tab_line_top"),
    vert: realHeader.querySelector(".tab_line_vert"),
    right: realHeader.querySelector(".tab_line_right"),
    short: realHeader.querySelector(".tab_line_short"),
    circle: realHeader.querySelector(".g_large_dot"),
    arrow: realHeader.querySelector(".tab_line_polygon"),
  };

  let tl = gsap.timeline({
    scrollTrigger: {
      ...scrollTriggerDefaults,
      trigger: element,
      markers: isStaging,
    },
    defaults: {
      ease: "none",
    },
  });

  tl.from(lines.top, {
    scaleX: 0,
  });

  tl.from(
    lines.vert,
    {
      scaleY: 0,
    },
    ">-=1",
  );

  tl.from(lines.right, {
    scaleX: 0,
  });

  tl.from(lines.short, {
    scaleY: 0,
  });

  tl.from(
    lines.circle,
    {
      scale: 0.8,
    },
    ">-=1",
  );
  tl.to("body", {
    opacity: 1,
  });

  // Polygon (arrow) — merged into tl so it shares the same scroll clock
  tl.to(lines.arrow, { y: `+=${lines.vert.clientHeight}` }, 0.2);
  tl.set(lines.arrow, { rotateZ: 0, y: "-=2px" }, 1.4);
  tl.to(lines.arrow, { x: `+=${lines.right.clientWidth}` }, 1.4);
  tl.set(lines.arrow, { rotateZ: 90, x: "-=1px" }, 2.6);
  tl.to(lines.arrow, { y: `+=${lines.short.clientHeight}` }, 2.6);

  if (window.innerWidth < 991) return;

  // --- Stacked card effect ---
  const cardsList = element.querySelector(".tabs_cards_list");
  const cards = Array.from(element.querySelectorAll(".tabs_card"));
  if (!cardsList || cards.length < 2) return;

  gsap.set(cardsList, { position: "relative" });
  gsap.set(cards, { position: "relative" });

  const GAP = 88;
  const REVEAL_GAP = 8; // 1rem gap after the revealed card
  let activeIndex = -1;
  const tweens = [];

  // Cache stacked margins per card (recalculated on resize)
  let stackedMargins = [];

  function calcMargins() {
    stackedMargins = cards.map((card, i) => {
      if (i === 0) return 0;
      return -(cards[i - 1].offsetHeight - GAP);
    });
  }

  function applyLayout(animate = false) {
    const duration = animate ? 0.4 : 0;

    cards.forEach((card, i) => {
      // z-index: ascending so later cards sit on top in the stack
      const z = i + 1;

      if (i === 0) {
        gsap.to(card, { zIndex: z, duration: 0 });
        return;
      }

      // The card right after the active card gets its overlap removed
      // so the active card is fully visible
      const isRevealing = activeIndex !== -1 && i === activeIndex + 1;
      const mt = isRevealing ? REVEAL_GAP : stackedMargins[i];

      const t = gsap.to(card, {
        marginTop: mt,
        zIndex: z,
        duration,
        ease: "power2.out",
      });
      if (animate) tweens.push(t);
    });

    if (animate && tweens.length) {
      tweens[tweens.length - 1].then(() => Scroll.resize());
    }
  }

  function setActive(index) {
    activeIndex = index === activeIndex ? -1 : index;
    applyLayout(true);
  }

  // Initial layout
  calcMargins();
  applyLayout();

  // Event handlers
  function handleEnter(i) {
    return () => {
      if (i === activeIndex) return;
      tweens.push(
        gsap.to(cards[i], { y: "-1rem", duration: 0.3, ease: "power2.out" }),
      );
    };
  }

  function handleLeave(i) {
    return () => {
      if (i === activeIndex) return;
      tweens.push(
        gsap.to(cards[i], { y: 0, duration: 0.3, ease: "power2.out" }),
      );
    };
  }

  function handleClick(i) {
    return () => setActive(i);
  }

  const listeners = [];
  cards.forEach((card, i) => {
    const enter = handleEnter(i);
    const leave = handleLeave(i);
    const click = handleClick(i);
    card.addEventListener("mouseenter", enter);
    card.addEventListener("mouseleave", leave);
    card.addEventListener("click", click);
    listeners.push({ card, enter, leave, click });
  });

  // Resize — recalculate margins and reapply
  const removeResize = Resize.add(() => {
    calcMargins();
    applyLayout();
  });

  // Cleanup
  onDestroy(() => {
    listeners.forEach(({ card, enter, leave, click }) => {
      card.removeEventListener("mouseenter", enter);
      card.removeEventListener("mouseleave", leave);
      card.removeEventListener("click", click);
    });
    tweens.forEach((t) => t.kill());
    tl.kill();
    removeResize();
  });
}
